import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dates';
import { t } from '../../utils/translations';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../../components/KebabMenu';
import '../Products.css';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    client_ids: [],
    status: 'active'
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        axios.get('/api/users'),
        currentUser?.role === 'super_admin' ? axios.get('/api/clients') : Promise.resolve({ data: { clients: [] } })
      ]);

      setUsers(usersRes.data.users || []);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert(t('Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const toggleClientId = (clientId) => {
    setFormData((prev) => {
      const has = prev.client_ids.includes(clientId);
      return {
        ...prev,
        client_ids: has
          ? prev.client_ids.filter((id) => id !== clientId)
          : [...prev.client_ids, clientId]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        username: formData.username,
        email: formData.email?.trim() || null,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role,
        status: formData.status,
        client_ids: formData.client_ids,
        client_id: formData.client_ids[0] || null
      };

      if (currentUser?.role === 'user') {
        payload.role = 'user';
        payload.client_ids = [currentUser?.client_id].filter(Boolean);
        payload.client_id = currentUser?.client_id || null;
      }

      if (!editingUser) {
        if (!payload.password) {
          alert(t('Password is required for new users'));
          return;
        }
      } else if (!payload.password) {
        delete payload.password;
      }

      if (currentUser?.role === 'super_admin' && payload.role !== 'super_admin' && payload.client_ids.length === 0) {
        alert('Select at least one client');
        return;
      }

      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, payload);
      } else {
        await axios.post('/api/users', payload);
      }

      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save user'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      full_name: user.full_name || '',
      role: user.role || 'user',
      client_ids: Array.isArray(user.client_ids) && user.client_ids.length
        ? user.client_ids
        : (user.client_id ? [user.client_id] : []),
      status: user.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (id === currentUser.id) {
      alert(t('You cannot delete your own account'));
      return;
    }

    if (!window.confirm(t('Are you sure you want to delete this user?'))) {
      return;
    }

    try {
      await axios.delete(`/api/users/${id}`);
      fetchData();
    } catch (error) {
      alert(t('Failed to delete user'));
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'user',
      client_ids: currentUser?.role === 'super_admin' ? [] : [currentUser?.client_id].filter(Boolean),
      status: 'active'
    });
  };

  const openModal = () => {
    resetForm();
    setEditingUser(null);
    setShowModal(true);
  };

  const clientLabel = (user) => {
    if (user.client_names?.length) return user.client_names.join(', ');
    if (user.client_id) return `${t('Client')} #${user.client_id.substring(0, 8)}`;
    return t('Super Admin');
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('User Management')}</h1>
          {currentUser?.role !== 'user' && (
            <button onClick={openModal} className="btn btn-primary">
              {t('Add User')}
            </button>
          )}
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('Username')}</th>
                <th>{t('Email')}</th>
                <th>{t('Full Name')}</th>
                <th>{t('Role')}</th>
                <th>{t('Client')}</th>
                <th>{t('Status')}</th>
                <th>{t('Created')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>{t('No users found')}</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.full_name}</td>
                    <td>
                      <span className={`status-badge status-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{clientLabel(user)}</td>
                    <td>
                      <span className={`status-badge status-${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      {currentUser?.role === 'user' ? (
                        <span style={{ color: '#666', fontSize: '12px' }}>{t('View Only')}</span>
                      ) : (
                        <KebabMenu
                          menuId={`user-${user.id}`}
                          openId={openActionMenu}
                          setOpenId={setOpenActionMenu}
                          label={t('Actions')}
                          items={[
                            { label: t('Edit'), icon: MENU_ICONS.edit, onClick: () => handleEdit(user) },
                            user.id !== currentUser.id && {
                              label: t('Delete'),
                              icon: MENU_ICONS.trash,
                              danger: true,
                              onClick: () => handleDelete(user.id)
                            }
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingUser ? t('Edit User') : t('Add User')}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('Username')} *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>{t('Email')} (optional)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>{t('Full Name')} *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('Password')} {editingUser ? `(${t('leave blank to keep current')})` : '*'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
                {currentUser?.role === 'admin' && currentUser?.field_orders_enabled && (
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={formData.role === 'salesman' ? 'salesman' : 'user'}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="user">{t('User')}</option>
                      <option value="salesman">Salesman</option>
                    </select>
                    <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                      A salesman uses the mobile app to take orders in the market.
                    </small>
                  </div>
                )}
                {currentUser?.role === 'super_admin' && (
                  <div className="form-group">
                    <label>{t('Role')}</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="user">{t('User')}</option>
                      <option value="salesman">Salesman</option>
                      <option value="admin">{t('Admin')}</option>
                      <option value="super_admin">{t('Super Admin')}</option>
                    </select>
                  </div>
                )}
                {currentUser?.role === 'super_admin' && formData.role !== 'super_admin' && (
                  <div className="form-group">
                    <label>Clients * (select one or more)</label>
                    <div style={{
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      padding: 10,
                      maxHeight: 180,
                      overflowY: 'auto'
                    }}>
                      {clients.length === 0 ? (
                        <div style={{ color: '#666' }}>No clients found</div>
                      ) : (
                        clients.map((client) => (
                          <label
                            key={client.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.client_ids.includes(client.id)}
                              onChange={() => toggleClientId(client.id)}
                            />
                            <span>
                              {client.alias_name || client.name}
                              {client.alias_name && client.name && client.alias_name !== client.name
                                ? ` (${client.name})`
                                : ''}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>{t('Status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="inactive">{t('Inactive')}</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
